import type { ComponentType } from 'react'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import type { DeliveryMode } from '@/lib/dr-types'

interface ModeStyle {
  label: string
  icon: ComponentType<{ className?: string; strokeWidth?: number }>
  className: string
}

/**
 * Delivery mode chips (plans/03 A5.5; artifact `SUPPLY.glyph`). Air, road and
 * sea use the mode ink on its wash, matching the arc, line and dashed line on
 * the Operations Globe.
 */
const MODES: Record<DeliveryMode, ModeStyle> = {
  helicopter: { label: 'Air', icon: Icons.plane, className: 'bg-mode-air-wash text-mode-air-ink' },
  road: { label: 'Road', icon: Icons.truck, className: 'bg-mode-road-wash text-mode-road-ink' },
  ship: { label: 'Sea', icon: Icons.ship, className: 'bg-mode-sea-wash text-mode-sea-ink' },
  ship_foot: { label: 'Sea and on foot', icon: Icons.ship, className: 'bg-mode-sea-wash text-mode-sea-ink' },
  rail: { label: 'Rail', icon: Icons.train, className: 'bg-muted text-foreground' },
  foot: { label: 'On foot', icon: Icons.footprints, className: 'bg-muted text-foreground' },
}

export function modeLabel(mode: DeliveryMode): string {
  return MODES[mode]?.label ?? mode
}

export function ModeChip({ mode, className }: { mode: DeliveryMode; className?: string }) {
  const style = MODES[mode]
  if (!style) return null
  const Icon = style.icon
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
        style.className,
        className
      )}
    >
      <Icon className='h-3.5 w-3.5' strokeWidth={1.5} aria-hidden='true' />
      {style.label}
    </span>
  )
}
